import * as React from 'react';
import { createLocators } from '@u1/locators';
import clsx from 'clsx';

import { Avatar } from '../../atoms/Avatar';
import { Checkbox } from '../../atoms/Checkbox';
import { Typography } from '../../atoms/Typography';
import useStyles from './Option.styles';

export interface IOptionProps<T> {
  /**
   * Generic value to be held by the component. Can accept any value
   */
  value: T;
  /**
   * Option label
   */
  label?: React.ReactNode;

  /**
   * Option subtext
   */
  subtext?: React.ReactNode;
  /**
   * Disables option interactions
   */
  disabled?: boolean;
  /**
   * Highlights option if active
   */
  selected?: boolean;
  /**
   * Renders checkbox inside option component
   */
  withCheckbox?: boolean;

  /**
   * Renders slot for icon inside option component.
   */
  icon?: React.ReactNode;
  /**
   * Link for avatar image.
   */
  avatar?: string;
  /**
   * OnClick callback. Returns value
   */
  onClick?: (value: T) => void;
  /**
   * Escape hatch to add styles to the option
   */
  _UNSAFE_classname?: string;
}

export const optionLocators = createLocators({
  name: 'Option',
  componentLocators: ['option', 'label', 'subtext', 'icon'],
});
const { locators } = optionLocators();

export const Option = <T,>({
  label,
  subtext,
  value,
  onClick,
  children,
  disabled,
  selected,
  withCheckbox,
  icon,
  avatar,
  _UNSAFE_classname,
}: React.PropsWithChildren<IOptionProps<T>>) => {
  const classes = useStyles();

  return (
    <div
      {...locators.option({ disabled, selected })}
      className={clsx(_UNSAFE_classname, {
        [classes.root]: true,
        [classes.disabled]: disabled,
        [classes.selected]: selected,
      })}
      onClick={onClick?.bind(null, value)}
      tabIndex={disabled ? -1 : 0}
    >
      {children ? (
        <Typography
          locatorsOverrides={locators.label()}
          as="div"
          className={classes.label}
        >
          {children}
        </Typography>
      ) : (
        <>
          {withCheckbox || icon || avatar ? (
            <div className={classes.icons}>
              {withCheckbox ? (
                <Checkbox
                  readOnly
                  checked={selected}
                  _UNSAFE_classname={classes.checkbox}
                />
              ) : null}
              {icon ? icon : null}
              {avatar ? <Avatar size={20} source={avatar} fallback /> : null}
            </div>
          ) : null}
          <div className={classes.content}>
            <Typography
              locatorsOverrides={locators.label()}
              as="div"
              className={classes.label}
            >
              {children || label}
            </Typography>
            <Typography
              locatorsOverrides={locators.subtext()}
              as="div"
              className={classes.subtext}
            >
              {subtext}
            </Typography>
          </div>
        </>
      )}
    </div>
  );
};

export default Option;
